import { Observable } from "rxjs";
import { skipWhile, first } from "rxjs/operators";

import auth from "@canopytax/cp-client-auth";
import { onlineListener as onlineObservable } from "online-listener";

import { defaultToAPIUrl } from "./url-prefix.helpers.js";
import {
  setHeaders,
  CanopyError,
  handleUnknownError,
  applyPreAuthHeader,
  resolveCredentials,
  normalizeBody,
  trackCall,
} from "./fetcher.helpers.js";
import {
  decodeSse,
  StreamDecodeError,
  type DecodedEvent,
  type WrapperEvent,
  type KeepaliveEvent,
} from "./stream-decoders";

// A yielded stream event is one of:
//   - DecodedEvent<T>: object payloads spread to the root (the typical case).
//   - WrapperEvent: non-object payloads (primitives, parseEventData:false raw
//     strings) wrapped as { type, data, _streamMeta? }.
//   - KeepaliveEvent: SSE comment-line heartbeats. Carry a `__keepalive: true`
//     brand so they're type-safely discriminable from a server-emitted
//     `event: keepalive\ndata: ...` frame (which would land in WrapperEvent
//     with the same `type` value but no brand).
//
// To filter heartbeats: `if ("__keepalive" in event) continue;`
export type StreamEvent<T> = DecodedEvent<T> | WrapperEvent | KeepaliveEvent;

export type StreamResult<T> = AsyncIterable<StreamEvent<T>> & {
  asObservable: () => Observable<StreamEvent<T>>;
};

// Public types

export type ReconnectOptions = {
  maxAttempts?: number;
  initialDelayMs?: number;
  maxDelayMs?: number;
  onReconnect?: (info: { attempt: number; lastEventId?: string }) => void;
};

// Narrow shapes for headers/body/credentials. We use these instead of the DOM
// lib's HeadersInit/BodyInit/RequestCredentials aliases because those are
// TypeScript-only types that eslint's no-undef rule doesn't recognize.
type StreamRequestHeaders = Headers | Record<string, string>;
type StreamRequestBody = string | FormData | Blob | Record<string, unknown>;
type StreamRequestCredentials = "omit" | "same-origin" | "include";

export type StreamOptions = {
  method?: string;
  headers?: StreamRequestHeaders;
  body?: StreamRequestBody;
  credentials?: StreamRequestCredentials;
  signal?: AbortSignal;
  reconnect?: boolean | ReconnectOptions;
  parseEventData?: boolean;
  passThrough401?: boolean;
};

const DEFAULT_RECONNECT: Required<ReconnectOptions> = {
  maxAttempts: 5,
  initialDelayMs: 1000,
  maxDelayMs: 30000,
  onReconnect: () => {},
};

// The fields we populate on fetch's second argument.
type PreparedRequestInit = {
  method: string;
  headers: Headers;
  body: string | FormData | Blob | undefined;
  credentials: StreamRequestCredentials;
  signal: AbortSignal | undefined;
};

type PreparedRequest = { url: string; init: PreparedRequestInit };

// Build a fetch request from user options: prefix the URL, apply the standard
// Canopy header set (auth/CSRF/feature-toggles), normalize the body, resolve
// credentials. Returns the URL and init object — does not call fetch.
export function prepareRequest(
  url: string,
  options: StreamOptions,
  lastEventId: string | undefined
): PreparedRequest {
  const finalUrl = defaultToAPIUrl(url);
  const headers = new Headers(options.headers);

  // Apply Canopy's standard auth/CSRF/feature-toggle headers.
  setHeaders((key: string, value: string) => headers.set(key, value));

  // SSE reconnect: the server-assigned event id from the previous attempt is
  // sent back so the server can resume the stream where it dropped.
  if (lastEventId) {
    headers.set("Last-Event-ID", lastEventId);
  }

  const preAuthSessionId = applyPreAuthHeader(headers);
  const credentials = resolveCredentials(
    options.credentials,
    !!preAuthSessionId
  ) as StreamRequestCredentials;

  // normalizeBody returns string | FormData | Blob | undefined for the inputs
  // we accept (StreamRequestBody). Cast at the boundary so the rest of the file
  // doesn't have to widen back through unknown.
  const body = normalizeBody(options.body, headers) as
    | string
    | FormData
    | Blob
    | undefined;

  return {
    url: finalUrl,
    init: {
      method: options.method ?? "GET",
      headers,
      body,
      credentials,
      signal: options.signal,
    },
  };
}

// Internal: online wait. Resolves on the first `online: true` emission.
//
// Honors an optional AbortSignal: if the caller aborts (e.g. component
// unmount, user cancel) while we're still offline, the wait rejects with
// an AbortError instead of hanging forever. The abort listener is wired
// with `{ once: true }` and removed on the success path so the signal
// doesn't accumulate listeners across reconnect attempts.
export async function waitOnline(signal?: AbortSignal): Promise<void> {
  if (signal?.aborted) {
    throw new DOMException("Aborted", "AbortError");
  }
  const online$ = onlineObservable.pipe(
    skipWhile((online: boolean) => !online),
    first()
  );
  if (!signal) {
    await online$.toPromise();
    return;
  }
  await new Promise<void>((resolve, reject) => {
    const onAbort = () => {
      sub.unsubscribe();
      reject(new DOMException("Aborted", "AbortError"));
    };
    const sub = online$.subscribe(
      () => {
        signal.removeEventListener("abort", onAbort);
        resolve();
      },
      (err: unknown) => {
        signal.removeEventListener("abort", onAbort);
        reject(err);
      }
    );
    signal.addEventListener("abort", onAbort, { once: true });
  });
}

// Open the stream connection. On 401, refresh the auth token and retry
// once; a second 401 throws. Other failures (4xx, 5xx, network, null body)
// throw immediately. The orchestrator above decides whether the error is
// retryable and whether to reconnect.
export async function openWithReauth(
  url: string,
  options: StreamOptions,
  lastEventId: string | undefined,
  alreadyRefreshed = false
): Promise<Response> {
  await waitOnline(options.signal);

  // Track on the user-shaped (url, options) tuple. Tracking the prepared
  // init wouldn't catch duplicates: each call constructs a fresh Headers
  // instance, and lodash isEqual sees those as distinct objects.
  const tracking = trackCall([url, options]);
  if (tracking.isInfiniteLoop) {
    throw new CanopyError(
      `Infinite requests detected from the client! ${tracking.count} in the last 30 seconds.`
    );
  }

  const prepared = prepareRequest(url, options, lastEventId);
  const response = await fetch(prepared.url, prepared.init);

  if (response.status === 401 && !options.passThrough401 && !alreadyRefreshed) {
    await auth.refreshAuthToken({ clientSecret: "TaxUI:f7fsf29adsy9fg" });
    return openWithReauth(url, options, lastEventId, true);
  }

  if (!response.ok) {
    const err = new CanopyError(`Stream open failed (${response.status})`);
    err.status = response.status;
    throw err;
  }

  if (!response.body) {
    throw new CanopyError(
      "Stream open succeeded but response.body is null (environment lacks ReadableStream support)"
    );
  }

  return response;
}

export { DEFAULT_RECONNECT };

// ── Reconnect option normalization ──────────────────────────────

function normalizeReconnect(
  opt: boolean | ReconnectOptions | undefined
): Required<ReconnectOptions> | false {
  if (opt === false) return false;
  if (opt === true || opt === undefined) return DEFAULT_RECONNECT;
  return { ...DEFAULT_RECONNECT, ...opt };
}

// ── Retry classification ────────────────────────────────────────
//
// Streams retry on transient failures only. AbortError is honored as a
// caller-driven cancel and never retried. StreamDecodeError signals a
// malformed payload — that's a server-side bug we want to surface, not
// reconnect through. HTTP 5xx is treated as transient (server hiccup,
// deploy, load-shed). HTTP 4xx is a contract violation reconnecting can't
// fix (404 = wrong URL, 401 already had its one-shot refresh in
// openWithReauth, 403 = perms). Errors with no status — network failures,
// abrupt disconnects — are assumed retryable so a flaky network doesn't
// surface as a permanent failure to the consumer.
export function isRetryable(err: unknown): boolean {
  if (err instanceof Error && err.name === "AbortError") return false;
  if (err instanceof StreamDecodeError) return false;
  const status = (err as { status?: number })?.status;
  if (typeof status === "number") {
    return status >= 500 && status < 600;
  }
  return true;
}

// Internal: signal-aware delay used between reconnect attempts. Resolves
// after `ms`, or rejects with AbortError if the consumer aborts before
// the timer fires. The abort listener is wired with `{ once: true }` and
// removed on the success path so reconnects don't accumulate listeners.
function delay(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException("Aborted", "AbortError"));
      return;
    }
    const onAbort = () => {
      clearTimeout(timer);
      reject(new DOMException("Aborted", "AbortError"));
    };
    const timer = setTimeout(() => {
      signal?.removeEventListener("abort", onAbort);
      resolve();
    }, ms);
    signal?.addEventListener("abort", onAbort, { once: true });
  });
}

// ── Main loop ───────────────────────────────────────────────────
//
// Wraps openWithReauth and decodeSse in a reconnect loop. Successful events
// reset backoff; failures escalate exponentially, capped at maxDelayMs and
// maxAttempts. Server-sent SSE `retry:` overrides the next delay (delivered
// via the decoder's onRetry callback, not via yielded events).
// Last-Event-ID is propagated across reconnects so SSE servers can resume
// from the last successfully delivered event.
export async function* runStream<T = unknown>(
  url: string,
  options: StreamOptions
): AsyncGenerator<StreamEvent<T>> {
  const reconnect = normalizeReconnect(options.reconnect);
  const parseEventData = options.parseEventData ?? true;

  let attempt = 0;
  let retryMs = reconnect === false ? 0 : reconnect.initialDelayMs;
  // serverRetry holds the most recent server-sent retry: hint. Tracked
  // separately from retryMs so backoff resets after a successful event
  // honor the server's hint rather than reverting to initialDelayMs.
  let serverRetry: number | undefined;
  let lastEventId: string | undefined;

  while (true) {
    try {
      const response = await openWithReauth(url, options, lastEventId);
      // Hand the consumer's signal to the decoder so a mid-stream abort
      // unblocks the in-flight reader.read() (via reader.cancel()) rather
      // than hanging until the source produces another chunk. onRetry
      // captures server-sent retry: hints out-of-band — they update the
      // next reconnect delay without polluting the consumer's event stream.
      for await (const event of decodeSse<T>(response.body!, {
        parseEventData,
        signal: options.signal,
        onRetry: (ms) => {
          serverRetry = ms;
        },
      })) {
        // A successful event resets backoff state — without this, a failure
        // cascade on an earlier reconnect would compound forever, leaving the
        // stream perpetually slow to reconnect even after long stretches of
        // healthy delivery. Server retry: hints win over initialDelayMs.
        attempt = 0;
        if (reconnect !== false) {
          retryMs = serverRetry ?? reconnect.initialDelayMs;
        }

        if (event._streamMeta?.id) lastEventId = event._streamMeta.id;

        yield event;

        // Caller-side abort fired between yields. Returning here ends the
        // generator cleanly without another decoder round-trip; the for-await
        // loop's exit calls the decoder's finally (which cancels the reader).
        if (options.signal?.aborted) return;
      }
      return; // clean end of stream
    } catch (err) {
      // Caller-driven cancel: return silently. No throw so consumers can
      // `for await` without wrapping in try/catch for the abort case.
      if (options.signal?.aborted) return;

      const canRetry =
        reconnect !== false &&
        isRetryable(err) &&
        attempt < reconnect.maxAttempts;

      if (!canRetry) {
        // Non-retryable or exhausted: route through the same Sentry-on-unknown
        // funnel the one-shot fetcher uses, then re-throw so the consumer's
        // `for await` rejects. handleUnknownError throws synchronously for
        // CanopyError and returns a rejected promise otherwise — we ignore
        // both: we only want its side effects (mark showToast=false, send
        // to Sentry) and rely on our own `throw err` for the sole rejection
        // path. Swallowing the returned promise is safe because we re-throw
        // synchronously on the same tick.
        try {
          const result = handleUnknownError(err);
          // Suppress unhandled rejection on the promise returned for non-CanopyErrors.
          if (result && typeof result.catch === "function") {
            result.catch(() => {});
          }
        } catch {
          // CanopyError path — already going to throw below.
        }
        throw err;
      }

      attempt++;
      reconnect.onReconnect({ attempt, lastEventId });

      try {
        await delay(Math.min(retryMs, reconnect.maxDelayMs), options.signal);
      } catch {
        // delay() rejected because the signal fired during backoff. Treat
        // the same as the top-of-catch abort check: silent return.
        return;
      }
      retryMs = Math.min(retryMs * 2, reconnect.maxDelayMs);
    }
  }
}

// ── Public API ──────────────────────────────────────────────────
//
// fetchAsStream wraps runStream as a StreamResult — an AsyncIterable<T>
// with an asObservable() method. Lazy: each iteration / subscribe opens a
// fresh request, so multiple consumers each get their own stream rather
// than implicitly fanning out from one connection.
export function fetchAsStream<T = unknown>(
  url: string,
  options: StreamOptions = {}
): StreamResult<T> {
  return {
    [Symbol.asyncIterator]() {
      return runStream<T>(url, options);
    },
    asObservable() {
      return makeObservable<T>(url, options);
    },
  };
}

// Bridges an AsyncGenerator into an RxJS Observable. Each subscribe spins up
// a fresh runStream so subscribers don't share state. Unsubscribing aborts
// the underlying generator via gen.return(), which propagates through the
// decoder's finally to cancel the in-flight reader.
function makeObservable<T>(
  url: string,
  options: StreamOptions
): Observable<StreamEvent<T>> {
  return new Observable<StreamEvent<T>>((subscriber) => {
    const gen = runStream<T>(url, options);
    let cancelled = false;

    (async () => {
      try {
        for await (const value of gen) {
          if (cancelled) return;
          subscriber.next(value);
        }
        if (!cancelled) subscriber.complete();
      } catch (err) {
        if (!cancelled) subscriber.error(err);
      }
    })();

    return () => {
      cancelled = true;
      // gen.return() ends the generator, which runs the decoder's finally
      // (cancels the reader) and stops the orchestrator's loop. Swallow any
      // settlement — we no longer care, and it could otherwise surface as
      // an unhandled rejection.
      gen.return(undefined as unknown as StreamEvent<T>).catch(() => {});
    };
  });
}
